import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, ButtonBase, Card, CardContent, CircularProgress,
  Divider, IconButton, Typography,
} from '@mui/material';
import { EditRounded } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { getNodeTypeConfig } from '../nodeTypes';
import { Node } from '../../types';

interface Props {
  node: Node;
  index: number;
  onIconClick?: () => void;
  onEditOpen: () => void;
  children?: React.ReactNode;
  // Overrides nodeTypes.tsx's per-type `stateless` flag. Needed for node types that
  // are stateless on one gateway kind but not another (e.g. 'fan' over SwitchBot IR
  // has no read-back, but over a Mijia gateway /api/command confirms real state) —
  // see FanNodeCard, which passes `gateway.kind !== 'mijia'`.
  statelessOverride?: boolean;
}

export const NodeCardShell: React.FC<Props> = ({
  node, index, onIconClick, onEditOpen, children, statelessOverride,
}) => {
  const { t } = useLanguage();
  const { Icon, stateless: statelessDefault } = getNodeTypeConfig(node.type);
  const stateless = statelessOverride ?? statelessDefault;
  const Wrapper: React.ElementType = onIconClick ? ButtonBase : Box;

  // Stateless (IR) nodes have no readable on/off state — show a transient
  // "command sent" confirmation after a successful command instead.
  const [sent, setSent] = useState(false);
  const prevLoading = useRef(false);
  useEffect(() => {
    const was = prevLoading.current;
    prevLoading.current = !!node.isLoading;
    if (stateless && was && !node.isLoading && !node.error) {
      setSent(true);
      const id = setTimeout(() => setSent(false), 2000);
      return () => clearTimeout(id);
    }
  }, [node.isLoading, node.error, stateless]);

  // Status row: {color, label} or null to hide it entirely.
  let status: { color: string; label: string } | null;
  if (node.error) {
    status = { color: '#EF5350', label: node.error };
  } else if (stateless) {
    status = sent ? { color: '#4CAF50', label: t.commandSent } : null;
  } else if (node.state === true) {
    status = { color: '#4CAF50', label: t.statusOn };
  } else if (node.state === false) {
    status = { color: '#757575', label: t.statusOff };
  } else {
    status = { color: '#FFA726', label: t.statusUnknown };
  }

  return (
    <Card elevation={2} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={children ? 1.5 : 0}>
          {/* On cards with no on/off buttons of their own this avatar is the
              only way to switch the device, so there it has to be a real
              button -- named, keyboard-operable, focus-ringed. Cards that
              carry their own controls pass no onIconClick, and the avatar
              stays a plain decorative Box rather than an empty button. */}
          <Wrapper
            {...(onIconClick ? {
              onClick: onIconClick,
              disabled: !!node.isLoading,
              'aria-label': t.a11yToggleNode(node.label || t.nodeDefaultLabel(index)),
            } : {})}
            sx={{ borderRadius: '50%', cursor: onIconClick ? 'pointer' : 'default', flexShrink: 0 }}
          >
            {node.isLoading ? (
              <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={36} color="primary" />
              </Box>
            ) : (
              <Avatar sx={{
                width: 64,
                height: 64,
                background: 'radial-gradient(circle at 35% 35%, #787878, #3a3a3a)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.5)',
              }}>
                <Icon sx={{ fontSize: 32, color: '#c8c8c8' }} />
              </Avatar>
            )}
          </Wrapper>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" noWrap sx={{ flex: 1 }}>
                {node.label || t.nodeDefaultLabel(index)}
              </Typography>
              <IconButton size="small" aria-label={t.a11yEditNode} onClick={onEditOpen} sx={{ ml: 0.5, flexShrink: 0 }}>
                <EditRounded fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {node.description || t.nodeDefaultDescription(index)}
            </Typography>
            {/* The dot is decorative -- the label beside it carries the state,
                and role=status announces it when it changes. */}
            {status && (
              <Box display="flex" alignItems="center" gap={0.75} mt={0.5} role="status">
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: status.color }}>
                  {status.label}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {children && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            {children}
          </>
        )}
      </CardContent>
    </Card>
  );
};
